import { ref, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { ConfirmationModal } from '@/shared/ui';
import CertificateIcon from '@/shared/assets/icons/CertificateIcon.svg';
import { extractApiErrorMessage, useCustomToast } from '@/shared/lib';
import { trainingCollaboratorApi } from '@/entities/Training';

export const CERTIFICATE_BATCH_CONFIRM_TITLE = 'Deseja gerar os certificados do treinamento?';
export const CERTIFICATE_BATCH_CONFIRM_TEXT =
  'Os certificados serão gerados automaticamente apenas para os colaboradores com status \'Concluído\'. Caso existam certificados gerados anteriormente, estes serão sobrescritos.';
export const CERTIFICATE_BATCH_SUCCESS_MESSAGE = 'Certificados gerados com sucesso';
export const CERTIFICATE_BATCH_EMPTY_MESSAGE =
  'Nenhum certificado gerado. Nenhum colaborador concluiu o treinamento';
export const CERTIFICATE_BATCH_ERROR_MESSAGE = 'Erro ao gerar certificados. Tente novamente';
/**
 * The batch handler tolerates a per-collaborator failure (PDF/MinIO) and only throws
 * when NONE were generated, so `generatedCount < eligibleCount` with both above zero
 * is a real, silent partial failure — the success toast alone would hide it.
 */
export const CERTIFICATE_BATCH_PARTIAL_MESSAGE = (generatedCount: number, eligibleCount: number) =>
  `${generatedCount} de ${eligibleCount} certificados gerados. Alguns colaboradores não puderam ser processados`;

interface UseCertificateBatchGenerationOptions {
  /** Reads the training id on screen. Null while it is still loading. */
  trainingId: () => number | null;
  /**
   * Announces a successful batch, so the paginated Colaboradores tab can reload
   * and pick up the collaborators whose "Visualizar Certificado" icon just enabled.
   */
  onGenerated?: () => void;
}

export function useCertificateBatchGeneration(options: UseCertificateBatchGenerationOptions): {
  isGenerating: Ref<boolean>;
  requestBatchGeneration: () => void;
} {
  const { showSuccessToast, showWarningToast, showErrorToast } = useCustomToast();

  const isGenerating = ref(false);

  /**
   * The in-flight guard lives here as well as in the modal, mirroring
   * useCompleteTraining: a second confirm can never start a second request even if
   * the click reaches this by another route. On failure the modal stays open with
   * the button re-enabled.
   */
  const confirmBatchGeneration = async () => {
    const trainingId = options.trainingId();
    if (isGenerating.value || trainingId === null) return;

    isGenerating.value = true;

    try {
      const { data } = await trainingCollaboratorApi.generateTrainingCertificatesBatch(trainingId);
      batchModal.close();

      if (data.eligibleCount === 0) {
        showWarningToast({ title: CERTIFICATE_BATCH_EMPTY_MESSAGE, type: 'warning' });
      } else if (data.generatedCount < data.eligibleCount) {
        showWarningToast({
          title: CERTIFICATE_BATCH_PARTIAL_MESSAGE(data.generatedCount, data.eligibleCount),
          type: 'warning',
        });
      } else {
        showSuccessToast({ title: CERTIFICATE_BATCH_SUCCESS_MESSAGE, type: 'success' });
      }

      options.onGenerated?.();
    } catch (error) {
      showErrorToast(
        {
          title: CERTIFICATE_BATCH_ERROR_MESSAGE,
          message: extractApiErrorMessage(error),
          type: 'error',
        },
        { timeout: false, closeButton: true }
      );
    } finally {
      isGenerating.value = false;
    }
  };

  const batchModal = useModal({
    component: ConfirmationModal,
    attrs: {
      icon: CertificateIcon,
      iconAlt: 'Ícone de certificado',
      title: CERTIFICATE_BATCH_CONFIRM_TITLE,
      text: CERTIFICATE_BATCH_CONFIRM_TEXT,
      actionButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      get isSubmitting() {
        return isGenerating.value;
      },
      onClose() {
        if (isGenerating.value) return;
        batchModal.close();
      },
      async onConfirm() {
        await confirmBatchGeneration();
      },
    },
  });

  const requestBatchGeneration = () => {
    batchModal.open();
  };

  return { isGenerating, requestBatchGeneration };
}

export default useCertificateBatchGeneration;
